import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useInterval, useUnmount } from 'react-use'
import { useAudioEngine } from 'player'
import { trigger } from '../../../lib/events'
import { Separate, Summary } from '../../../types'
import { ContextShell } from '../../../../../../packages/web-app-shell/src'

interface Props {
  summary?: Summary
}

export const useUpgradabilityNotificationsV2 = ({ summary }: Props): void => {
  const { i18n } = useLingui()
  const {
    user,
    toast: { add: addToast, onClearFixedToast }
  } = useContext(ContextShell)
  const audioEngine = useAudioEngine((p: any) => p.engine)

  const [hasSeparateProcessing, setHasSeparateProcessing] = useState(false)
  const [updateType, setUpdateType] = useState<Separate['updateType'] | null>(
    null
  )

  const isFree = useMemo(
    () => !user?.subscription?.isPremium && !user?.subscription?.isPro,
    [user]
  )
  const updateInProgress = useMemo(
    () => summary?.separate?.stems?.some((s) => s?.adding),
    [summary]
  )

  const dispatchAlert = useCallback(() => {
    addToast({
      type: 'default',
      icon: null,
      fixed: true,
      closable: true,
      buttonText: i18n._(t`apply_button`),
      description: i18n._(t`new_version_ready`),
      onConfirm: () => trigger('player:reset-page')
    })
  }, [i18n, addToast])

  useEffect(() => {
    if (hasSeparateProcessing) {
      const separate = summary?.separate
      if (!updateInProgress && separate?.status === 'COMPLETED') {
        if (updateType === 'ADAPT') {
          dispatchAlert()
        } else if (isFree || audioEngine === 'wavesjs') {
          trigger('player:reset-page')
        }
      }
    }
  }, [
    isFree,
    summary,
    updateType,
    audioEngine,
    updateInProgress,
    hasSeparateProcessing,
    dispatchAlert
  ])

  useEffect(() => {
    if (updateInProgress) {
      setHasSeparateProcessing(true)
      setUpdateType(summary?.separate?.updateType)
    }
  }, [summary, updateInProgress])

  useInterval(
    () => {
      if (!updateInProgress) {
        setHasSeparateProcessing(false)
      }
    },
    hasSeparateProcessing ? 2000 : null
  )

  useUnmount(() => {
    onClearFixedToast()
  })
}
